import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import {
  ArrowLeft, Upload, PanelLeft, AlertTriangle, Minus, Plus, X, FileText, Eye, Code2,
  Sparkles, Play, Check, ChevronDown, Settings, Layers, Copy,
} from "lucide-react";
import { Spinner, copyTextToClipboard } from "../../constants";
import DownloadCSVButton from "../../DownloadCSVButton";
import {
  fieldTypeMeta,
  confidenceStyle,
  fileExtension,
  isEmptyValue,
  STUDIO_TEST_FILE_ACCEPT,
} from "../studioConst";
import { SX, FRAME, TEST_PANES } from "../studioTokens";
import PipelineSettings from "./PipelineSettings";
import FieldEditor from "./FieldEditor";
import { buildFieldHistory } from "./fieldHistory";

pdfjs.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/" + pdfjs.version + "/pdf.worker.min.js";

// Keyed by ExtractionJob.status while the container polls /extraction-status/.
const STAGE_LABELS = {
  queued: "Queued for extraction…",
  ingesting: "Ingesting document…",
  extracting: "Extracting fields…",
};

const useStoredWidth = (key, def) => {
  const [w, setW] = useState(() => { try { const v = parseInt(localStorage.getItem(key), 10); return isNaN(v) ? def : v; } catch { return def; } });
  useEffect(() => { try { localStorage.setItem(key, String(w)); } catch {} }, [key, w]);
  return [w, setW];
};

const PanelToggle = ({ open, onClick, side }) => (
  <button onClick={onClick} title={`${open ? "Hide" : "Show"} ${side} panel`}
    className={`w-[30px] h-[30px] rounded-lg border border-[#ECE9EE] flex items-center justify-center flex-shrink-0 transition-colors ${open ? "bg-[#FBF5FA] text-[#893F82]" : "bg-white text-[#8A8A90] hover:bg-[#F5F5F5]"}`}
    style={{ transform: side === "right" ? "scaleX(-1)" : "none" }}>
    <PanelLeft size={15} />
  </button>
);

/* 05 · Test screen — full-width, three drag-resizable columns:
   Schema · Document viewer · Results. Panels collapse when the viewport can no
   longer give the centre column its 380px minimum. */
const TestWorkspace = ({
  template, templateName, slug, config, onConfigChange, onOpenEngineModal, onExportJson,
  docs, activeDocId, onSetActive, onAddFiles, onRemoveDoc, onToggleDoc,
  onRunExtraction, testing,
  onAddField, onRename, onChangeType, onChangeInstruction, onChangeSchema, onDelete, onToggleEnable,
  dirty, saving, onSaveToTemplate, onBack,
}) => {
  const fields = template.fields || [];
  const [leftW, setLeftW] = useStoredWidth("studio_test_left", TEST_PANES.left);
  const [rightW, setRightW] = useStoredWidth("studio_test_right", TEST_PANES.right);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [docView, setDocView] = useState("pdf");
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(72);
  const [baseW, setBaseW] = useState(600);
  const [dragging, setDragging] = useState(false);
  const [resultTab, setResultTab] = useState("formatted");
  const [jsonCopied, setJsonCopied] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const rowRef = useRef(null);
  const centerRef = useRef(null);
  const fileRef = useRef(null);

  const activeDoc = docs.find((d) => d.id === activeDocId) || docs[0];
  const fileProp = useMemo(() => activeDoc?.file || null, [activeDoc]);
  const activeFileType = fileExtension(fileProp);
  const activeIsPdf = activeFileType === "pdf";
  const selectedCount = docs.filter((d) => d.on).length;

  const measure = useCallback(() => { const w = centerRef.current?.clientWidth; if (w) setBaseW(Math.min(w - 48, 900)); }, []);
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure, leftOpen, rightOpen, leftW, rightW]);
  useEffect(() => {
    setPage(1);
    setNumPages(0);
    setDocView(activeIsPdf ? "pdf" : "text");
  }, [activeDocId, activeIsPdf]);

  // Collapse the side panels rather than squeezing the viewer below its minimum.
  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (leftOpen && rightOpen && w < leftW + rightW + TEST_PANES.centerMin + 12) { setRightOpen(false); return; }
      if (leftOpen && !rightOpen && w < leftW + TEST_PANES.centerMin + 6) setLeftOpen(false);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [leftOpen, rightOpen, leftW, rightW]);

  const startResize = (which) => (e) => {
    e.preventDefault();
    const sx = e.clientX, sl = leftW, sr = rightW;
    const move = (ev) => {
      if (which === "left") setLeftW(Math.max(260, Math.min(520, sl + (ev.clientX - sx))));
      else setRightW(Math.max(280, Math.min(560, sr - (ev.clientX - sx))));
    };
    const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); document.body.style.cursor = ""; };
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", move); document.addEventListener("mouseup", up);
  };

  const Resizer = ({ which }) => (
    <div onMouseDown={startResize(which)} className="w-1.5 flex-shrink-0 cursor-col-resize relative group z-[5]">
      <div className="absolute left-[2px] top-0 bottom-0 w-px bg-[#ECE9EE] group-hover:bg-[#E9D2E6]" />
    </div>
  );

  const pickFiles = () => fileRef.current?.click();
  const onFilesChosen = (e) => { onAddFiles(e.target.files); e.target.value = ""; };

  // Results are a join of the schema (order, type) with the active doc's payload.
  const results = activeDoc?.result && !activeDoc.result._error
    ? fields.map((f) => {
        const value = activeDoc.result[f.field_key];
        return { field: f, key: f.field_key, type: f.data_type, value, empty: isEmptyValue(value) };
      })
    : null;
  const emptyCount = results ? results.filter((r) => r.empty).length : 0;
  const resultJSON = () => JSON.stringify(activeDoc?.result || {}, null, 2);
  const copyResultJSON = () => { copyTextToClipboard(resultJSON()).catch(() => {}); setJsonCopied(true); setTimeout(() => setJsonCopied(false), 1400); };

  const pxWidth = Math.round(baseW * (zoom / 100));
  const pages = activeDoc?.ingestData?.pages || [];
  const runDisabled = testing || selectedCount === 0 || dirty;

  const viewTab = (on) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-semibold transition-colors ${
      on ? "bg-white text-[#1A1A1A] shadow-[0_1px_3px_rgba(0,0,0,0.08)]" : "text-[#52525B]"
    }`;
  const iconBtn = "w-[30px] h-[30px] rounded-md border border-[#ECE9EE] bg-white text-[#52525B] hover:bg-[#F5F5F5] flex items-center justify-center transition-colors";

  return (
    <div className={`flex flex-col flex-1 min-h-0 font-outfit ${FRAME.canvas}`} onClick={() => pipelineOpen && setPipelineOpen(false)}>
      <input ref={fileRef} type="file" accept={STUDIO_TEST_FILE_ACCEPT} multiple className="hidden" onChange={onFilesChosen} />

      {/* Top bar */}
      <div className={`${SX.topbar} gap-3`}>
        <button onClick={onBack} title="Back to template"
          className="w-9 h-9 flex items-center justify-center rounded-lg text-[#1A1A1A] hover:bg-[#F5F5F5] transition-colors flex-shrink-0">
          <ArrowLeft size={20} />
        </button>
        <div className={`w-px h-6 ${SX.divider}`} />
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-[16px] font-semibold text-[#1A1A1A] truncate">{templateName || "Untitled template"}</span>
          {slug && <span className="font-mono text-[11.5px] text-[#8A8A90] bg-[#FAF9FB] border border-[#ECE9EE] rounded-md px-2 py-0.5 whitespace-nowrap flex-shrink-0">/{slug}</span>}
        </div>
        <div className="flex-1" />
        <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button className={`${SX.btn} ${SX.btnGhost}`} onClick={() => setPipelineOpen((v) => !v)}>
            <Settings size={13} /> Pipeline Settings <ChevronDown size={11} className="opacity-60" />
          </button>
          {pipelineOpen && (
            <PipelineSettings config={config} onConfigChange={onConfigChange}
              onOpenEngineModal={() => { setPipelineOpen(false); onOpenEngineModal(); }}
              slug={slug} onExportJson={onExportJson} />
          )}
        </div>
        {dirty && (
          <div className="relative flex-shrink-0">
            <button onClick={onSaveToTemplate} disabled={saving}
              className={`${SX.btn} bg-[#2F7D44] text-white border-none shadow-[0_2px_6px_rgba(0,0,0,0.08)] hover:enabled:bg-[#276437]`}>
              {saving ? <><Spinner size={14} /> Saving…</> : <><Check size={14} /> Save changes</>}
            </button>
          </div>
        )}
        <button onClick={onRunExtraction} disabled={runDisabled}
          className={`${SX.btn} ${runDisabled ? "bg-[#F5F5F5] text-[#8A8A90] border-none cursor-not-allowed" : SX.btnPrimary}`}
          title={dirty ? "Save your changes to run extraction" : selectedCount === 0 ? "Select at least one document" : undefined}>
          {testing ? <><Spinner size={14} /> Running…</> : <><Play size={14} fill="currentColor" /> Run Extraction ({selectedCount})</>}
        </button>
      </div>

      {/* Columns */}
      <div ref={rowRef} className="flex-1 flex min-h-0">
        {/* LEFT — Schema */}
        {leftOpen && (
          <>
            <div className="flex-shrink-0 bg-white flex flex-col min-h-0" style={{ width: leftW }}>
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#ECE9EE] flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <span className="text-[11px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase">Schema</span>
                  <span className="px-2.5 py-0.5 rounded-full bg-[#FBF5FA] text-[#893F82] text-[11.5px] font-semibold">{fields.length}</span>
                  {dirty && <span title="Unsaved changes" className="w-[7px] h-[7px] rounded-full bg-[#C77A1C]" />}
                </div>
                <PanelToggle open onClick={() => setLeftOpen(false)} side="left" />
              </div>
              <div className="flex-1 overflow-auto">
                {fields.map((f, i) => (
                  <FieldEditor
                    key={f.field_key}
                    index={i}
                    field={{ ...f, __history: buildFieldHistory(f, template.refinement_history, template.origin) }}
                    context={template}
                    onRename={onRename}
                    onChangeType={onChangeType}
                    onChangeInstruction={onChangeInstruction}
                    onChangeSchema={onChangeSchema}
                    onDelete={onDelete}
                    onToggleEnable={onToggleEnable}
                  />
                ))}
              </div>
              <div className="p-3 border-t border-[#ECE9EE] flex-shrink-0">
                <button onClick={onAddField}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-white border border-dashed border-[#E9D2E6] rounded-[9px] text-[#893F82] text-[13px] font-semibold hover:bg-[#FBF5FA] transition-colors">
                  <Plus size={14} /> Add Field
                </button>
              </div>
            </div>
            <Resizer which="left" />
          </>
        )}

        {/* CENTER — Document viewer */}
        <div ref={centerRef} className={`flex-1 flex flex-col min-h-0 relative ${FRAME.canvas}`} style={{ minWidth: TEST_PANES.centerMin }}>
          {/* File strip */}
          <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-[#ECE9EE] bg-white flex-shrink-0 overflow-x-auto">
            {!leftOpen && <PanelToggle open={false} onClick={() => setLeftOpen(true)} side="left" />}
            {docs.map((d) => (
              <div key={d.id} onClick={() => onSetActive(d.id)}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-[9px] border-[1.5px] cursor-pointer flex-shrink-0 transition-colors ${d.id === activeDoc?.id ? "border-[#A04A97] bg-[#FBF5FA]" : "border-[#ECE9EE] bg-white"}`}>
                <input type="checkbox" checked={d.on} onClick={(e) => e.stopPropagation()} onChange={() => onToggleDoc(d.id)} className="w-4 h-4 accent-[#A04A97]" />
                {d.ingesting ? <Spinner size={13} /> : <FileText size={13} className={d.id === activeDoc?.id ? "text-[#893F82]" : "text-[#8A8A90]"} />}
                <span className={`text-[12.5px] font-semibold max-w-[180px] truncate ${d.id === activeDoc?.id ? "text-[#893F82]" : "text-[#1A1A1A]"}`}>{d.name}</span>
                <button onClick={(e) => { e.stopPropagation(); onRemoveDoc(d.id); }} className="w-[18px] h-[18px] rounded-full text-[#8A8A90] hover:text-[#B33A1E] flex items-center justify-center"><X size={11} /></button>
              </div>
            ))}
            <button onClick={pickFiles}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[9px] border border-dashed border-[#ECE9EE] bg-white text-[#52525B] text-[12.5px] font-semibold flex-shrink-0 hover:border-[#E9D2E6] hover:text-[#893F82] transition-colors">
              <Plus size={13} /> Add Files
            </button>
          </div>

          {docs.length === 0 ? (
            <div
              className={`flex-1 flex flex-col items-center justify-center gap-4 m-6 rounded-[14px] border-2 border-dashed cursor-pointer transition-colors ${dragging ? "border-[#A04A97] bg-[#FBF5FA]" : "border-[#ECE9EE] hover:border-[#E9D2E6] hover:bg-[#FBF5FA]"}`}
              onClick={pickFiles}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); onAddFiles(e.dataTransfer.files); }}
            >
              <div className="w-16 h-16 rounded-[14px] bg-[#F7EBF5] border border-[#E9D2E6] flex items-center justify-center text-[#A04A97]"><Upload size={28} /></div>
              <div className="text-[16px] font-semibold text-[#1A1A1A]">Drop documents to test</div>
              <p className="text-[12.5px] text-[#52525B] max-w-[380px] text-center leading-[1.6]">
                Upload PDFs or supported office/text documents. They're ingested on upload; press <b className="font-semibold">Run Extraction</b> to test the current schema against them.
              </p>
              <span className="text-[11.5px] bg-white text-[#52525B] px-4 py-1.5 rounded-full border border-[#ECE9EE]">PDF · DOCX · TXT · XLSX · RTF · images</span>
            </div>
          ) : (
            <>
              {/* Viewer toolbar */}
              <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#ECE9EE] bg-white flex-shrink-0">
                <div className="flex bg-[#F5F5F5] rounded-lg p-0.5">
                  <button onClick={() => activeIsPdf && setDocView("pdf")} disabled={!activeIsPdf} className={`${viewTab(docView === "pdf")} ${!activeIsPdf ? "opacity-40 cursor-not-allowed" : ""}`}>
                    <Eye size={13} className={docView === "pdf" ? "text-[#A04A97]" : "text-[#52525B]"} /> PDF View
                  </button>
                  <button onClick={() => setDocView("text")} className={viewTab(docView === "text")}
                    title="The processed text the model actually reads to run the extraction.">
                    <Code2 size={13} className={docView === "text" ? "text-[#A04A97]" : "text-[#52525B]"} /> AI Text View
                  </button>
                </div>
                <div className="flex-1" />
                {docView === "pdf" && numPages > 1 && (
                  <div className="flex items-center gap-1">
                    {Array.from({ length: numPages }, (_, i) => i + 1).map((pn) => (
                      <button key={pn} onClick={() => setPage(pn)}
                        className={`w-7 h-7 rounded-md text-[12px] font-semibold border transition-colors ${page === pn ? "border-[#A04A97] bg-[#FBF5FA] text-[#893F82]" : "border-[#ECE9EE] bg-white text-[#52525B] hover:bg-[#F5F5F5]"}`}>{pn}</button>
                    ))}
                    <div className={`w-px h-5 mx-1 ${SX.divider}`} />
                  </div>
                )}
                {!rightOpen && <PanelToggle open={false} onClick={() => setRightOpen(true)} side="right" />}
              </div>

              <div className="flex-1 overflow-auto flex justify-center items-start py-6">
                {activeDoc?.ingesting ? (
                  <div className="flex items-center gap-2 text-[#52525B] text-[13px] mt-16"><Spinner size={16} /> Ingesting document…</div>
                ) : !activeDoc?.ingestData ? (
                  <div className="flex flex-col items-center gap-2 text-center text-[#52525B] mt-16 px-8">
                    <AlertTriangle size={22} className="text-[#C77A1C]" />
                    <div className="text-[13px] max-w-[320px] leading-[1.5]">Ingestion failed for this document. Remove it and try again.</div>
                  </div>
                ) : docView === "pdf" && activeIsPdf ? (
                  fileProp ? (
                    <Document file={fileProp} onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                      loading={<div className="flex items-center gap-2 text-[#52525B] text-[13px] mt-12"><Spinner size={16} /> Loading…</div>}
                      error={<div className="text-[#B33A1E] text-[13px] mt-12">Couldn't render this PDF.</div>}>
                      <div className="relative shadow-[0_4px_18px_rgba(0,0,0,0.14)] bg-white rounded-[4px] overflow-hidden">
                        <Page pageNumber={page} width={pxWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                      </div>
                    </Document>
                  ) : (
                    <div className="text-[#52525B] text-[13px] mt-16">No preview available.</div>
                  )
                ) : (
                  <div className="w-full max-w-[760px] flex flex-col gap-4 px-6">
                    {!activeIsPdf && (
                      <div className={`${SX.cardSm} px-4 py-3 text-[12.5px] text-[#52525B] leading-[1.5]`}>
                        <b className="text-[#1A1A1A]">{activeDoc?.name}</b> is a {activeFileType.toUpperCase() || "document"} file. PDF preview is unavailable, but the processed text below is what the extraction pipeline uses.
                      </div>
                    )}
                    {pages.length === 0 ? (
                      <div className="text-center text-[#8A8A90] text-[13px] mt-12">No text extracted from this document.</div>
                    ) : pages.map((pg, i) => {
                      const pn = pg?.page_number ?? i + 1;
                      const txt = typeof pg === "string" ? pg : pg?.text ?? "";
                      return (
                        <div key={pn} className={`${SX.cardSm} overflow-hidden`}>
                          <div className="px-3.5 py-2 text-[11px] font-semibold text-[#8A8A90] uppercase tracking-[0.06em] border-b border-[#ECE9EE] flex items-center gap-1.5">
                            <FileText size={11} /> Page {pn}
                          </div>
                          <pre className="px-5 py-4 font-mono text-[12px] text-[#52525B] whitespace-pre-wrap break-words leading-[1.8] m-0">{txt}</pre>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {docView === "pdf" && (
                <div className="absolute bottom-[18px] right-[18px] flex items-center gap-1 bg-white border border-[#ECE9EE] rounded-[10px] p-1 shadow-[0_6px_20px_rgba(0,0,0,0.16)] z-10">
                  <button onClick={() => setZoom((z) => Math.max(40, z - 8))} className={iconBtn + " !border-none"}><Minus size={14} /></button>
                  <span className="text-[12.5px] font-semibold text-[#1A1A1A] w-[42px] text-center">{zoom}%</span>
                  <button onClick={() => setZoom((z) => Math.min(140, z + 8))} className={iconBtn + " !border-none"}><Plus size={14} /></button>
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT — Results */}
        {rightOpen ? (
          <>
            <Resizer which="right" />
            <div className="flex-shrink-0 bg-white flex flex-col min-h-0" style={{ width: rightW }}>
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#ECE9EE] flex-shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-[11px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase">Results</span>
                  {results && (
                    <span className="text-[11.5px] font-semibold text-[#52525B] truncate">
                      {results.length - emptyCount} of {results.length} extracted{emptyCount > 0 ? ` · ${emptyCount} empty` : ""}
                    </span>
                  )}
                </div>
                <PanelToggle open onClick={() => setRightOpen(false)} side="right" />
              </div>

              {activeDoc?.extracting ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-7">
                  <Spinner size={30} />
                  <div className="text-[14px] font-semibold text-[#1A1A1A] mt-4">Extracting…</div>
                  <div className="text-[12.5px] text-[#8A8A90] mt-1">{STAGE_LABELS[activeDoc.stage] || `Running ${fields.length} fields across ${selectedCount} document${selectedCount === 1 ? "" : "s"}.`}</div>
                </div>
              ) : activeDoc?.result?._error ? (
                <div className="flex items-start gap-2 text-[#B33A1E] text-[13px] p-4 leading-[1.5]">
                  <AlertTriangle size={14} className="mt-px flex-shrink-0" /> {activeDoc.result._error}
                </div>
              ) : !results ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-7">
                  <div className="w-14 h-14 rounded-[14px] bg-[#FBF5FA] flex items-center justify-center text-[#A04A97] mb-4"><Sparkles size={26} /></div>
                  <div className="text-[15px] font-semibold text-[#1A1A1A] mb-1.5">Run an extraction</div>
                  <div className="text-[13px] text-[#8A8A90] leading-[1.5] max-w-[240px]">Select your documents and click Run Extraction to see structured results here.</div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 px-4 pt-3">
                    <div className="flex bg-[#F5F5F5] rounded-lg p-0.5 flex-1">
                      <button onClick={() => setResultTab("formatted")} className={`flex-1 justify-center ${viewTab(resultTab === "formatted")}`}>
                        <Layers size={13} className={resultTab === "formatted" ? "text-[#A04A97]" : "text-[#52525B]"} /> Formatted
                      </button>
                      <button onClick={() => setResultTab("json")} className={`flex-1 justify-center ${viewTab(resultTab === "json")}`}>
                        <Code2 size={13} className={resultTab === "json" ? "text-[#A04A97]" : "text-[#52525B]"} /> JSON
                      </button>
                    </div>
                    <DownloadCSVButton data={activeDoc.result} templateName={templateName || "Extraction"} />
                  </div>

                  {resultTab === "formatted" ? (
                    <div className="flex-1 overflow-auto px-4 pt-3.5 pb-5 flex flex-col gap-2.5">
                      {results.map((r) => {
                        const tm = fieldTypeMeta(r.type);
                        const cs = confidenceStyle(r.field);
                        const disp = r.type === "table"
                          ? `${Array.isArray(r.value) ? r.value.length : 0} rows`
                          : (r.empty ? null : (typeof r.value === "object" ? JSON.stringify(r.value) : String(r.value)));
                        return (
                          <div key={r.key}
                            title={r.empty ? "No value found" : undefined}
                            className={`border-[1.5px] rounded-[11px] px-3.5 py-3 transition-all border-[#ECE9EE] bg-white ${r.empty ? "cursor-default" : ""}`}>
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className={`w-[17px] h-[17px] rounded flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${tm.badge}`}>{tm.glyph}</span>
                                <span className="font-mono text-[11px] font-semibold text-[#8A8A90] uppercase tracking-[0.03em] truncate">{r.key}</span>
                              </div>
                              <span className={`px-2 py-0.5 rounded-[5px] text-[9.5px] font-semibold flex-shrink-0 ${cs.badge}`}>{cs.label}</span>
                            </div>
                            <div className={`text-[14px] font-semibold leading-[1.4] break-words ${r.empty ? "text-[#8A8A90]" : "text-[#1A1A1A]"}`}>{r.empty ? "—" : disp}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col min-h-0 px-4 pt-3.5 pb-4">
                      <div className="flex justify-end mb-2">
                        <button onClick={copyResultJSON} className={`${SX.btn} ${SX.btnGhost} ${SX.btnSm}`}>
                          {jsonCopied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy JSON</>}
                        </button>
                      </div>
                      <pre className="flex-1 overflow-auto m-0 p-4 bg-[#1A1A1A] text-[#E9D2E6] rounded-[11px] text-[11.5px] leading-[1.7] font-mono whitespace-pre-wrap break-words">{resultJSON()}</pre>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="border-l border-[#ECE9EE] bg-white px-2.5 py-3.5 flex-shrink-0">
            <PanelToggle open={false} onClick={() => setRightOpen(true)} side="right" />
          </div>
        )}
      </div>
    </div>
  );
};

export default TestWorkspace;
